import { useRef, useState } from 'react'
import { Box, Stack, useTheme } from '@mui/material'

import DriverList from './driver-list/driver-list.component'
import AddDriverDialog from '../../../components/add-driver-dialog/add-driver-dialog.component'
import AddButton from 'components/add-button/add-button.component'
import SubscribeDriversDialog from 'components/subscription/subscribe-drivers-dialog.component'
import UpgradeSubscriptionDialog from 'components/subscription/upgrade-subscription-dialog.component'
import StripeCheckoutDialog from 'components/subscription/stripe-checkout-dialog.component'
import { getUserId } from 'utils/common'
import {
  useCompleteCheckoutMutation,
  useCreateCheckoutSessionMutation,
  useGetSubscriptionByUserIdQuery,
  useUpdateSubscriptionQuantityMutation,
} from 'store/subscriptions/subscriptions.api'
import {
  hasActiveSubscription,
  needsSubscriptionUpgrade,
} from 'store/subscriptions/subscriptions.types'

const DriverSelection = () => {
  const userId = getUserId()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [subscribeOpen, setSubscribeOpen] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [publishableKey, setPublishableKey] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [subscribeError, setSubscribeError] = useState<string | null>(null)
  const [upgradeError, setUpgradeError] = useState<string | null>(null)
  const sessionIdRef = useRef<string | null>(null)

  const { data: subscription, refetch } = useGetSubscriptionByUserIdQuery(
    { userId },
    { skip: !userId }
  )
  const [createCheckout, { isLoading: creatingCheckout }] =
    useCreateCheckoutSessionMutation()
  const [completeCheckout] = useCompleteCheckoutMutation()
  const [updateQuantity, { isLoading: updatingQuantity }] =
    useUpdateSubscriptionQuantityMutation()

  const handleAddClick = async () => {
    if (!userId) return
    setSubscribeError(null)

    let lookup = subscription
    if (!lookup) {
      try {
        lookup = await refetch().unwrap()
      } catch {
        setSubscribeOpen(true)
        setSubscribeError('Unable to load subscription. Please try again.')
        return
      }
    }

    if (!hasActiveSubscription(lookup)) {
      setSubscribeOpen(true)
      return
    }

    if (needsSubscriptionUpgrade(lookup)) {
      setUpgradeError(null)
      setUpgradeOpen(true)
      return
    }

    setAddDialogOpen(true)
  }

  const handleContinueSubscription = async (unit: number) => {
    if (!userId || !Number.isInteger(unit) || unit < 1) return
    setSubscribeError(null)
    try {
      const result = await createCheckout({ userId, unit }).unwrap()
      if (!result.clientSecret) {
        setSubscribeError('Unable to start checkout. Please try again.')
        return
      }
      setSessionId(result.sessionId)
      sessionIdRef.current = result.sessionId
      setClientSecret(result.clientSecret)
      setPublishableKey(result.publishableKey || null)
      setSubscribeOpen(false)
      setCheckoutOpen(true)
    } catch {
      setSubscribeError('Unable to start checkout. Please try again.')
    }
  }

  const handleCheckoutComplete = async () => {
    const completedSessionId = sessionIdRef.current || sessionId
    if (!completedSessionId) return
    try {
      await completeCheckout({ sessionId: completedSessionId }).unwrap()
      await refetch()
      setCheckoutOpen(false)
      setClientSecret(null)
      setSessionId(null)
      sessionIdRef.current = null
      setAddDialogOpen(true)
    } catch {
      setCheckoutOpen(false)
      setSubscribeOpen(true)
      setSubscribeError(
        'Payment finished, but we could not save the subscription. Please try again.'
      )
    }
  }

  const handleCheckoutClose = () => {
    setCheckoutOpen(false)
    setClientSecret(null)
    setSessionId(null)
    sessionIdRef.current = null
    setSubscribeOpen(true)
  }

  const handleUpgradeQuantity = async (nextUnit: number) => {
    if (!userId) return
    setUpgradeError(null)
    try {
      await updateQuantity({ userId, unit: nextUnit }).unwrap()
      setUpgradeOpen(false)
    } catch {
      setUpgradeError('Unable to upgrade subscription. Please try again.')
    }
  }

  const theme = useTheme()

  return (
    <Stack
      justifyContent="space-between"
      sx={{
        height: '100%',
        minHeight: 0,
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <DriverList />
      </Box>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          backgroundColor: theme.palette.common.white,
          borderTop: `1px solid ${theme.palette.divider}`,
          mt: 'auto',
          flexShrink: 0,
          pt: 0.5,
          pb: 1,
          px: 0.5,
          minHeight: 60,
        }}
      >
        <AddButton label="Add Driver" handleClick={handleAddClick} />
      </Box>
      <AddDriverDialog
        open={addDialogOpen}
        handleClose={() => setAddDialogOpen(false)}
      />
      <SubscribeDriversDialog
        open={subscribeOpen}
        initialUnit={subscription?.unit ?? 1}
        submitting={creatingCheckout}
        errorMessage={subscribeError}
        onClose={() => setSubscribeOpen(false)}
        onContinue={handleContinueSubscription}
      />
      <UpgradeSubscriptionDialog
        open={upgradeOpen}
        currentUnits={subscription?.unit ?? 0}
        unitPrice={subscription?.unitPrice}
        submitting={updatingQuantity}
        errorMessage={upgradeError}
        onClose={() => setUpgradeOpen(false)}
        onConfirm={handleUpgradeQuantity}
      />
      <StripeCheckoutDialog
        open={checkoutOpen}
        publishableKey={publishableKey}
        clientSecret={clientSecret}
        onClose={handleCheckoutClose}
        onComplete={handleCheckoutComplete}
      />
    </Stack>
  )
}

export default DriverSelection
